import { PurchaseOrderItem } from "@domain/purchasing";
import { IconButton, List, ListItem } from "@mui/material";
import { Button, DialogActions, DialogContent, Stack, useTheme } from "@mui/material";
import { Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { Close } from "@mui/icons-material";
import { usePurchaseOrderItems } from "@features/purchasing/hooks";
import { CreateShipmentItemListItem } from "./CreateShipmentItemListItem";
import DialogHeader from "@shared/ui/dialog/DialogHeader";



interface CreateShipmentItemProps {
    onNext: (selectedPurchaseOrderItems: PurchaseOrderItem[]) => void;
    onClose: () => void;
    purchaseOrderItems?: PurchaseOrderItem[];
}

export const CreateShipmentItems: React.FC<CreateShipmentItemProps> = (props: CreateShipmentItemProps) => {

    const theme = useTheme();

    const { purchaseOrderItems } = usePurchaseOrderItems({
        filters: {
            availableDaysAgo: 0
        }
    });

    const [selectedPurchaseOrderItems, setSelectedPurchaseOrderItems] = useState<PurchaseOrderItem[]>([]);

    useEffect(() => {
        setSelectedPurchaseOrderItems(props.purchaseOrderItems || []);
    }, [props.purchaseOrderItems]);

    const toggleSelectPurchaseOrderItem = (item: PurchaseOrderItem) => {
        if (isSelected(item)) {
            setSelectedPurchaseOrderItems(selectedPurchaseOrderItems.filter((selectedItem) => selectedItem.id !== item.id));
        } else {
            setSelectedPurchaseOrderItems([...selectedPurchaseOrderItems, item]);
        }
    }

    const isSelected = (item: PurchaseOrderItem) => {
        return selectedPurchaseOrderItems.some((selectedItem) => selectedItem.id === item.id);
    }

    const itemsWithoutShipment = purchaseOrderItems?.filter(item => !item.shipment) || [];
    const itemsWithShipment = purchaseOrderItems?.filter(item => item.shipment) || [];

    return (
        <>
            <DialogHeader >
                <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography variant="h6">Add items to the shipment</Typography>

                    <IconButton onClick={() => props.onClose()}>    <Close /> </IconButton>
                </Stack>

            </DialogHeader>

            <DialogContent sx={{ p: 0 }}>

                <List sx={{ p: 0 }}>
                    <ListItem sx={{
                        backgroundColor: theme.palette.background.default,
                        borderBottom: 1,
                        borderColor: theme.palette.divider,
                        py: 2,
                        px: 4,
                        position: 'sticky',
                        zIndex: 99,
                        top: 0
                    }}>
                        <Typography variant="subtitle2">Purchase Orders without shipments ({itemsWithoutShipment.length})</Typography>
                    </ListItem>

                    {itemsWithoutShipment.length === 0 && (
                        <ListItem sx={{ p: 4 }}>
                            <Typography variant="subtitle2" color="text.secondary">No purchase orders without shipments</Typography>
                        </ListItem>
                    )}

                    {itemsWithoutShipment.map((item) => (
                        <CreateShipmentItemListItem
                            key={item.id}
                            item={item}
                            selected={isSelected(item)}
                            onToggle={toggleSelectPurchaseOrderItem}
                        />
                    ))}
                </List>

                <List sx={{ p: 0 }}>
                    <ListItem sx={{
                        backgroundColor: theme.palette.background.default,
                        borderBottom: 1,
                        borderColor: theme.palette.divider,
                        py: 2,
                        px: 4,
                        position: 'sticky',
                        zIndex: 99,
                        top: 0
                    }}>
                        <Typography variant="subtitle2">Purchase Orders with shipments ({itemsWithShipment.length})</Typography>
                    </ListItem>

                    {itemsWithShipment.length === 0 && (
                        <ListItem sx={{ p: 4 }}>
                            <Typography variant="subtitle2" color="text.secondary">No purchase orders with shipments</Typography>
                        </ListItem>
                    )}

                    {itemsWithShipment.map((item) => (
                        <CreateShipmentItemListItem
                            key={item.id}
                            item={item}
                            selected={isSelected(item)}
                            onToggle={toggleSelectPurchaseOrderItem}
                        />
                    ))}
                </List>

            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: 1,
                    borderColor: theme.palette.divider,
                    px: 4,
                    py: 3,
                    gap: 2
                }}
            >
                <Button
                    onClick={() => props.onNext(selectedPurchaseOrderItems)}
                    variant={selectedPurchaseOrderItems.length > 0 ? 'contained' : 'outlined'}
                > {selectedPurchaseOrderItems.length > 0 ? 'Add items' : 'Create Shipment without items'}</Button>
            </DialogActions>
        </>
    );
};
